import config from "appConfig";

const theory = `<h3 class=sectionHead>Solutions of the wave equation for harmonic signals</h3>
<p>
   The solutions of the wave equation in time domain can be decomposed on the basis of sine and cosine functions (Fourier theorem). It is therefore appropriate to pay particular attention to cases in which the time signal is a pure tone (also known as harmonic or monochromatic wave by analogy with the light). In this case, the functions
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>f</mi>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>g</mi>
   </math>
   are the cosine and the general solution for a pure tone is thus written
<table class=equation>
   <tr>
      <td style="font-size: 10px  ">
         <math xmlns=http://www.w3.org/1998/Math/MathML class=equation display=block>
            <mi>p</mi>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mi>A</mi>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <mi>k</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>x</mi>
                  <mo class=MathClass-bin>−</mo>
                  <mi mathvariant=italic>ct</mi>
                  <mo class=MathClass-close>)</mo>
                  <mo class=MathClass-bin>−</mo>
                  <msup>
                     <mrow>
                        <mi>ϕ</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>+</mo>
                     </mrow>
                  </msup>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
            <mo class=MathClass-bin>+</mo>
            <mi>B</mi>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <mi>k</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>x</mi>
                  <mo class=MathClass-bin>+</mo>
                  <mi mathvariant=italic>ct</mi>
                  <mo class=MathClass-close>)</mo>
                  <mo class=MathClass-bin>+</mo>
                  <msup>
                     <mrow>
                        <mi>ϕ</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                     </mrow>
                  </msup>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
         </math>
      <td class=eq-no>(1)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>A</mi>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>B</mi>
   </math>
   are two constants that represent the amplitude of the fluctuations,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>ϕ</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>+</mo>
         </mrow>
      </msup>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>ϕ</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
         </mrow>
      </msup>
   </math>
   are terms of phase and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>k</mi>
   </math>
   is a constant inversion of a length, because the arguments of the cosine are dimensionless. This constant
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>k</mi>
   </math>
   is known as the wavenumber and is related to the pulsation
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ω</mi>
   </math>
   by the relation
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>k</mi>
      <mo class=MathClass-rel>=</mo>
      <mi>ω</mi>
      <mo class=MathClass-bin>∕</mo>
      <mi>c</mi>
   </math>. It is also recalled that the pulsation
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ω</mi>
   </math>
   is related to the frequency
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>f</mi>
   </math>
   and period
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>T</mi>
   </math>
   by the relations
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ω</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>2</mn>
      <mi mathvariant=italic>πf</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>2</mn>
      <mi>π</mi>
      <mo class=MathClass-bin>∕</mo>
      <mi>T</mi>
   </math>. In the same way the spatial period, i.e. the wavelength
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>λ</mi>
   </math>, is related to the wave number by the relation
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>k</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>2</mn>
      <mi>π</mi>
      <mo class=MathClass-bin>∕</mo>
      <mi>λ</mi>
   </math>
   . As
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>k</mi>
      <mo class=MathClass-rel>=</mo>
      <mi>ω</mi>
      <mo class=MathClass-bin>∕</mo>
      <mi>c</mi>
   </math>
   ,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>λ</mi>
   </math>
   is also related to frequency
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>f</mi>
   </math>
   by
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>λ</mi>
      <mo class=MathClass-rel>=</mo>
      <mi>c</mi>
      <mo class=MathClass-bin>∕</mo>
      <mi>f</mi>
      <mo class=MathClass-rel>=</mo>
      <mi mathvariant=italic>cT</mi>
   </math>.
<p>
   It is often preferred to write the general solution at pulsation
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ω</mi>
   </math>
   in the following form
<table class=equation>
   <tr>
      <td style="font-size: 10px">
         <math xmlns=http://www.w3.org/1998/Math/MathML class=equation display=block>
            <mi>p</mi>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mi>A</mi>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <mi mathvariant=italic>ωt</mi>
                  <mo class=MathClass-bin>−</mo>
                  <mi mathvariant=italic>kx</mi>
                  <mo class=MathClass-bin>+</mo>
                  <msup>
                     <mrow>
                        <mi>ϕ</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>+</mo>
                     </mrow>
                  </msup>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
            <mo class=MathClass-bin>+</mo>
            <mi>B</mi>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <mi mathvariant=italic>ωt</mi>
                  <mo class=MathClass-bin>+</mo>
                  <mi mathvariant=italic>kx</mi>
                  <mo class=MathClass-bin>+</mo>
                  <msup>
                     <mrow>
                        <mi>ϕ</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                     </mrow>
                  </msup>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
         </math>
      <td class=eq-no>(2)
</table>
<p>
   This form is equivalent to the one shown in (1) because the cosine function is even. For mathematical reasons that the cosine functions are difficult to handle (especially the fact that the derivative of a cosine is a sine), it is preferable to use easier to handle complex exponential (especially because the derivative of an exponential is an exponential). An imaginary part (which as its name suggests is perfectly imaginary) is therefore added to the actual signal by replacing the cosine exponential (indeed
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>jX</mi>
         </mrow>
      </msup>
      <mo class=MathClass-rel>=</mo>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mi>X</mi>
      <mo class=MathClass-bin>+</mo>
      <mi>ȷ</mi>
      <mi class=qopname>sin</mi>
      <mo>⁡</mo>
      <mi>X</mi>
   </math>
   ). The general complex solution at pulsation
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ω</mi>
   </math>
   is thus written
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML class=equation display=block>
            <mover accent=true>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mo accent=true>^</mo>
            </mover>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mi>Â</mi>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mi>ȷ</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi mathvariant=italic>ωt</mi>
                  <mo class=MathClass-bin>−</mo>
                  <mi mathvariant=italic>kx</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </msup>
            <mo class=MathClass-bin>+</mo>
            <mover accent=true>
               <mrow>
                  <mi>B</mi>
               </mrow>
               <mo accent=true>^</mo>
            </mover>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mi>ȷ</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi mathvariant=italic>ωt</mi>
                  <mo class=MathClass-bin>+</mo>
                  <mi mathvariant=italic>kx</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </msup>
         </math>
      <td class=eq-no>(3)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>Â</mi>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mover accent=true>
         <mrow>
            <mi>B</mi>
         </mrow>
         <mo accent=true>^</mo>
      </mover>
   </math>
   are also complex numbers:
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>Â</mi>
      <mo class=MathClass-rel>=</mo>
      <mrow>
         <mo fence=true form=prefix>|</mo>
         <mrow>
            <mi>Â</mi>
         </mrow>
         <mo fence=true form=postfix>|</mo>
      </mrow>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mi>ȷ</mi>
            <msup>
               <mrow>
                  <mi>ϕ</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>+</mo>
               </mrow>
            </msup>
         </mrow>
      </msup>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mover accent=true>
         <mrow>
            <mi>B</mi>
         </mrow>
         <mo accent=true>^</mo>
      </mover>
      <mo class=MathClass-rel>=</mo>
      <mrow>
         <mo fence=true form=prefix>|</mo>
         <mrow>
            <mover accent=true>
               <mrow>
                  <mi>B</mi>
               </mrow>
               <mo accent=true>^</mo>
            </mover>
         </mrow>
         <mo fence=true form=postfix>|</mo>
      </mrow>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mi>ȷ</mi>
            <msup>
               <mrow>
                  <mi>ϕ</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
               </mrow>
            </msup>
         </mrow>
      </msup>
   </math>. These two terms represent the complex amplitudes at
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>
   of the two waves and contain both amplitude information (their modules) and phase (their arguments). The above notation presents problems since only the real part of the result makes sense:
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>p</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mi>ℜ</mi>
      <mrow>
         <mo fence=true form=prefix>[</mo>
         <mrow>
            <mover accent=true>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mo accent=true>^</mo>
            </mover>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
         </mrow>
         <mo fence=true form=postfix>]</mo>
      </mrow>
   </math>. We will see that this is an important point to remember when calculating the acoustic intensity for example and that is why we recommend not to use the complex signal. The last expression can also be written
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML class=equation display=block>
            <mover accent=true>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mo accent=true>^</mo>
            </mover>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <mi>Â</mi>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi mathvariant=italic>ȷkx</mi>
                     </mrow>
                  </msup>
                  <mo class=MathClass-bin>+</mo>
                  <mover accent=true>
                     <mrow>
                        <mi>B</mi>
                     </mrow>
                     <mo accent=true>^</mo>
                  </mover>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>+</mo>
                        <mi mathvariant=italic>ȷkx</mi>
                     </mrow>
                  </msup>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ȷωt</mi>
               </mrow>
            </msup>
         </math>
      <td class=eq-no>(4)
</table>
<p>
   and since the term
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ȷωt</mi>
         </mrow>
      </msup>
   </math>
   is always factor we finally keep the expression
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML class=equation display=block>
            <mover accent=true>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mo accent=true>^</mo>
            </mover>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mi>Â</mi>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi mathvariant=italic>ȷkx</mi>
               </mrow>
            </msup>
            <mo class=MathClass-bin>+</mo>
            <mover accent=true>
               <mrow>
                  <mi>B</mi>
               </mrow>
               <mo accent=true>^</mo>
            </mover>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>+</mo>
                  <mi mathvariant=italic>ȷkx</mi>
               </mrow>
            </msup>
         </math>
      <td class=eq-no>(5)
</table>
<p>
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mover accent=true>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mo accent=true>^</mo>
      </mover>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   represents the complex amplitude of the oscillations at abcissa
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
   </math>
   for a pulsation
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ω</mi>
   </math>
   . This is totally different from
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>p</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   which represents the real signal in the time domain. When
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mover accent=true>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mo accent=true>^</mo>
      </mover>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   is used, all the quantities are considered in the frequency domain whereas when
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>p</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   is used all the quantities are considered in the time domain. It must be payed attention to the context, as in most of the literature, these quantities will be denoted
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>p</mi>
   </math>
   interchangeably in both situations, which may cause some confusion.
<h4 class=subsectionHead>
   Note on the use of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ȷωt</mi>
         </mrow>
      </msup>
   </math>
</h4>
<p>
   Note that, in the complex expression of the temporal signal, one could also choose to use
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mi mathvariant=italic>ȷωt</mi>
         </mrow>
      </msup>
   </math>
   instead of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ȷωt</mi>
         </mrow>
      </msup>
   </math>
   since the real part of these two quantities are equal. The two conventions are both used depending on the field: physics or engineering.
<p>
   Note also that when the Fourier transform is used both terms
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ȷωt</mi>
         </mrow>
      </msup>
   </math>
   (positive frequencies) and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mi mathvariant=italic>ȷωt</mi>
         </mrow>
      </msup>
   </math>
   (negative frequency) coexist. For a signal at pulsation
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ω</mi>
   </math>
   , the amplitude of the actual signal is indeed obtained by summing the complex amplitudes
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mover accent=true>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mo accent=true>^</mo>
      </mover>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mover accent=true>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mo accent=true>^</mo>
      </mover>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mo class=MathClass-bin>−</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   (for an analytic signal
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mover accent=true>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mo accent=true>^</mo>
      </mover>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   is the complex conjugate of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mover accent=true>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mo accent=true>^</mo>
      </mover>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mo class=MathClass-bin>−</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   and the sum is real). One must be careful because the amplitude in the sense of the Fourier transform is half the actual amplitude of the real signal, which is a source of error and confusion.
<h3 class=sectionHead>The wave equation in harmonic regime</h3>
<p>
   In harmonic regime (pure frequency), any signal
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>s</mi>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   can be written as
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML class=equation display=block>
            <mi>s</mi>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mi>ℜ</mi>
            <mrow>
               <mo fence=true form=prefix>[</mo>
               <mrow>
                  <mi>ŝ</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>ω</mi>
                  <mo class=MathClass-close>)</mo>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>jωt</mi>
                     </mrow>
                  </msup>
               </mrow>
               <mo fence=true form=postfix>]</mo>
            </mrow>
         </math>
      <td class=eq-no>(6)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ŝ</mi>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   represents the complex amplitude of the signal. The derivative with respect to time of any signal can be written as
<table class=equation>
   <tr>
      <td style="font-size: 13px">
         <math xmlns=http://www.w3.org/1998/Math/MathML class=equation display=block>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>∂s</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>t</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>∂t</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mi>ℜ</mi>
            <mrow>
               <mo fence=true form=prefix>[</mo>
               <mrow>
                  <mi>ŝ</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>ω</mi>
                  <mo class=MathClass-close>)</mo>
                  <mfrac>
                     <mrow>
                        <mi>∂</mi>
                        <msup>
                           <mrow>
                              <mi>e</mi>
                           </mrow>
                           <mrow>
                              <mi mathvariant=italic>ȷωt</mi>
                           </mrow>
                        </msup>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>∂t</mi>
                     </mrow>
                  </mfrac>
               </mrow>
               <mo fence=true form=postfix>]</mo>
            </mrow>
            <mo class=MathClass-rel>=</mo>
            <mi>ℜ</mi>
            <mrow>
               <mo fence=true form=prefix>[</mo>
               <mrow>
                  <mi mathvariant=italic>ȷωŝ</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>ω</mi>
                  <mo class=MathClass-close>)</mo>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>ȷωt</mi>
                     </mrow>
                  </msup>
               </mrow>
               <mo fence=true form=postfix>]</mo>
            </mrow>
         </math>
      <td class=eq-no>(7)
</table>
<p>
   Therefore the derivative with respect to time is equivalent to a multiplication by
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>ȷω</mi>
   </math>
   of the complex amplitude. Similarly, the integration with respect to time is equivalent to a division by
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>ȷω</mi>
   </math>
   and a second derivative with respect to time is equivalent to a multiplication by
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-bin>−</mo>
      <msup>
         <mrow>
            <mi>ω</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
   </math>. Thus, the wave equation in the time domain, that is
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML class=equation display=block>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>∂</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mover accent=true>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mo accent=true>^</mo>
                  </mover>
               </mrow>
               <mrow>
                  <mi>∂</mi>
                  <msup>
                     <mrow>
                        <mi>x</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-bin>−</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>∂</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mover accent=true>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mo accent=true>^</mo>
                  </mover>
               </mrow>
               <mrow>
                  <mi>∂</mi>
                  <msup>
                     <mrow>
                        <mi>t</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mn>0</mn>
         </math>
      <td class=eq-no>(8)
</table>
<p>can therefore be replaced by the wave equation in the frequency domain
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML class=equation display=block>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>∂</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mover accent=true>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mo accent=true>^</mo>
                  </mover>
               </mrow>
               <mrow>
                  <mi>∂</mi>
                  <msup>
                     <mrow>
                        <mi>x</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>ω</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mover accent=true>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mo accent=true>^</mo>
            </mover>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mn>0</mn>
         </math>
      <td class=eq-no>(9)
</table>
<p>This equation is called the Helmholtz equation (here one-dimensional).
<p>
   Equation (9) can be used to find the solutions of the wave equation in harmonic regime. We look for a solution of that equation in the form
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mover accent=true>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mo accent=true>^</mo>
      </mover>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mi>A</mi>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mi mathvariant=italic>ȷKx</mi>
         </mrow>
      </msup>
   </math>. From the Helmholtz equation, we obtain
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML class=equation display=block>
            <mo class=MathClass-bin>−</mo>
            <msup>
               <mrow>
                  <mi>K</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mover accent=true>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mo accent=true>^</mo>
            </mover>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>ω</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mover accent=true>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mo accent=true>^</mo>
            </mover>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mn>0</mn>
         </math>
      <td class=eq-no>(10)
</table>
<p>which leads to:
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML class=equation display=block>
            <msup>
               <mrow>
                  <mi>K</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>ω</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <msup>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
         </math>
      <td class=eq-no>(11)
</table>
<p>
   Two solutions are then found for
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>K</mi>
   </math>
   :
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>K</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>+</mo>
         </mrow>
      </msup>
      <mo class=MathClass-rel>=</mo>
      <mi>k</mi>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>K</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
         </mrow>
      </msup>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-bin>−</mo>
      <mi>k</mi>
   </math>. This leads to the two solutions found previously for the pressure
</p>
<math xmlns=http://www.w3.org/1998/Math/MathML display=block>
   <mtable class=align-star columnalign=left>
      <mtr>
         <mtd class=align-odd columnalign=right>
            <msup>
               <mrow>
                  <mover accent=true>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mo accent=true>^</mo>
                  </mover>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>+</mo>
               </mrow>
            </msup>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mi>Â</mi>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi mathvariant=italic>ȷkx</mi>
               </mrow>
            </msup>
            <mstyle class=text>
               <mtext> and </mtext>
            </mstyle>
            <msup>
               <mrow>
                  <mover accent=true>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mo accent=true>^</mo>
                  </mover>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
               </mrow>
            </msup>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mover accent=true>
               <mrow>
                  <mi>B</mi>
               </mrow>
               <mo accent=true>^</mo>
            </mover>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>+</mo>
                  <mi mathvariant=italic>ȷkx</mi>
               </mrow>
            </msup>
            <mo class=MathClass-punc>.</mo>
         </mtd>
         <mtd class=align-even></mtd>
      </mtr>
   </mtable>
</math>
<p>The Euler equation
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML class=equation display=block>
            <mfrac>
               <mrow>
                  <mi>∂</mi>
                  <mover accent=true>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mo accent=true>^</mo>
                  </mover>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>∂x</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mfrac>
               <mrow>
                  <mi>∂</mi>
                  <mover accent=true>
                     <mrow>
                        <mi>v</mi>
                     </mrow>
                     <mo accent=true>^</mo>
                  </mover>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>∂t</mi>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(12)
</table>
<p>can as well be written in the form
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML class=equation display=block>
            <mfrac>
               <mrow>
                  <mi>∂</mi>
                  <mover accent=true>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mo accent=true>^</mo>
                  </mover>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>∂x</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mo class=MathClass-bin>−</mo>
            <mi mathvariant=italic>jω</mi>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mover accent=true>
               <mrow>
                  <mi>v</mi>
               </mrow>
               <mo accent=true>^</mo>
            </mover>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
         </math>
      <td class=eq-no>(13)
</table>
<p>
   The Euler equation allows to find the particle velocity from the pressure. Indeed, for a wave of the form
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mover accent=true>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mo accent=true>^</mo>
            </mover>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>+</mo>
         </mrow>
      </msup>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mi>Â</mi>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mi mathvariant=italic>ȷkx</mi>
         </mrow>
      </msup>
   </math>, one has
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML class=equation display=block>
            <mfrac>
               <mrow>
                  <mi>∂</mi>
                  <msup>
                     <mrow>
                        <mover accent=true>
                           <mrow>
                              <mi>p</mi>
                           </mrow>
                           <mo accent=true>^</mo>
                        </mover>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>+</mo>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>∂x</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mo class=MathClass-bin>−</mo>
            <mi mathvariant=italic>ȷk</mi>
            <msup>
               <mrow>
                  <mover accent=true>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mo accent=true>^</mo>
                  </mover>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>+</mo>
               </mrow>
            </msup>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
         </math>
      <td class=eq-no>(14)
</table>
<p>leading to
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML class=equation display=block>
            <msup>
               <mrow>
                  <mover accent=true>
                     <mrow>
                        <mi>v</mi>
                     </mrow>
                     <mo accent=true>^</mo>
                  </mover>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>+</mo>
               </mrow>
            </msup>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mover accent=true>
                           <mrow>
                              <mi>p</mi>
                           </mrow>
                           <mo accent=true>^</mo>
                        </mover>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>+</mo>
                     </mrow>
                  </msup>
                  <mo class=MathClass-open>(</mo>
                  <mi>x</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mi>ω</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi>c</mi>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(15)
</table>
<p>
   which is the same result as the one seen in the time domain.
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>ρ</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mi>c</mi>
   </math>
   is the characteristic frequency. As well, for a wave of the form
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mover accent=true>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mo accent=true>^</mo>
            </mover>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
         </mrow>
      </msup>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mi>Â</mi>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ȷkx</mi>
         </mrow>
      </msup>
   </math>, one has
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mover accent=true>
               <mrow>
                  <mi>v</mi>
               </mrow>
               <mo accent=true>^</mo>
            </mover>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
         </mrow>
      </msup>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-bin>−</mo>
      <msup>
         <mrow>
            <mover accent=true>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mo accent=true>^</mo>
            </mover>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
         </mrow>
      </msup>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-bin>∕</mo>
      <mo class=MathClass-open>(</mo>
      <msub>
         <mrow>
            <mi>ρ</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mi>c</mi>
      <mo class=MathClass-close>)</mo>
   </math>.
<h3 class=sectionHead>Standing waves</h3>
<p>The general solution for the complex pressure amplitude may be written
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML class=equation display=block>
            <mover accent=true>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mo accent=true>^</mo>
            </mover>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mi>Â</mi>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi mathvariant=italic>ȷkx</mi>
               </mrow>
            </msup>
            <mo class=MathClass-bin>+</mo>
            <mover accent=true>
               <mrow>
                  <mi>B</mi>
               </mrow>
               <mo accent=true>^</mo>
            </mover>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ȷkx</mi>
               </mrow>
            </msup>
         </math>
      <td class=eq-no>(16)
</table>
<p>and the associated solution for the velocity is written
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML class=equation display=block>
            <mover accent=true>
               <mrow>
                  <mi>v</mi>
               </mrow>
               <mo accent=true>^</mo>
            </mover>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>Â</mi>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi mathvariant=italic>ȷkx</mi>
                     </mrow>
                  </msup>
                  <mo class=MathClass-bin>−</mo>
                  <mover accent=true>
                     <mrow>
                        <mi>B</mi>
                     </mrow>
                     <mo accent=true>^</mo>
                  </mover>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>ȷkx</mi>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi>c</mi>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(17)
</table>
<figure class=figure>
   <p>
      <img alt="Figure 1. Progressive waves to the right (A), to the left (B) and standing wave (A + B representing the pressure and A-B the particle velocity) at different time." src=${config.ASSETS_URL}/01/04/theory/figure-1.svg>
      <figcaption class=caption>Figure 1. Progressive waves to the right (A), to the left (B) and standing wave (A + B representing the pressure and A-B the particle velocity) at different time.</figcaption>
</figure>
<p>
   When
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mover accent=true>
         <mrow>
            <mi>B</mi>
         </mrow>
         <mo accent=true>^</mo>
      </mover>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>, a pure progressive wave moves to the right (<a href="#" data-img-alt="Figure 1. Progressive waves to the right (A), to the left (B) and standing wave (A + B representing the pressure and A-B the particle velocity) at different time." data-img-src=${config.ASSETS_URL}/01/04/theory/figure-1.svg>Figure 1</a> A), pressure and velocity are in phase. The amplitude is independent of the position and is equal to
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-rel>|</mo>
      <mi>Â</mi>
      <mo class=MathClass-rel>|</mo>
   </math>
   for the pressure. When
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>Â</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>, a pure progressive wave moves to the left (<a href="#" data-img-alt="Figure 1. Progressive waves to the right (A), to the left (B) and standing wave (A + B representing the pressure and A-B the particle velocity) at different time." data-img-src=${config.ASSETS_URL}/01/04/theory/figure-1.svg>Figure 1</a> B), pressure and velocity are opposite in phase. The amplitude is independent of the position and is equal to
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-rel>|</mo>
      <mover accent=true>
         <mrow>
            <mi>B</mi>
         </mrow>
         <mo accent=true>^</mo>
      </mover>
      <mo class=MathClass-rel>|</mo>
   </math>
   for the pressure. When
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-rel>|</mo>
      <mi>Â</mi>
      <mo class=MathClass-rel>|</mo>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-rel>|</mo>
      <mover accent=true>
         <mrow>
            <mi>B</mi>
         </mrow>
         <mo accent=true>^</mo>
      </mover>
      <mo class=MathClass-rel>|</mo>
   </math>, we have a pure standing wave (<a href="#" data-img-alt="Figure 1. Progressive waves to the right (A), to the left (B) and standing wave (A + B representing the pressure and A-B the particle velocity) at different time." data-img-src=${config.ASSETS_URL}/01/04/theory/figure-1.svg>Figure 1</a> A+B). This particularly important case deserves to be examined more closely. Considering
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>Â</mi>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-rel>|</mo>
      <mi>Â</mi>
      <mo class=MathClass-rel>|</mo>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mi>ȷ</mi>
            <msup>
               <mrow>
                  <mi>ϕ</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>+</mo>
               </mrow>
            </msup>
         </mrow>
      </msup>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mover accent=true>
         <mrow>
            <mi>B</mi>
         </mrow>
         <mo accent=true>^</mo>
      </mover>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-rel>|</mo>
      <mi>Â</mi>
      <mo class=MathClass-rel>|</mo>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mi>ȷ</mi>
            <msup>
               <mrow>
                  <mi>ϕ</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
               </mrow>
            </msup>
         </mrow>
      </msup>
   </math>, the pressure can be written
<table class=equation>
   <tr>
      <td style="font-size: 9px">
         <math xmlns=http://www.w3.org/1998/Math/MathML class=equation display=block>
            <mover accent=true>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mo accent=true>^</mo>
            </mover>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mo class=MathClass-rel>|</mo>
            <mi>Â</mi>
            <mo class=MathClass-rel>|</mo>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi mathvariant=italic>ȷkx</mi>
                  <mo class=MathClass-bin>+</mo>
                  <msup>
                     <mrow>
                        <mi>ϕ</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>+</mo>
                     </mrow>
                  </msup>
               </mrow>
            </msup>
            <mo class=MathClass-bin>+</mo>
            <mo class=MathClass-rel>|</mo>
            <mi>Â</mi>
            <mo class=MathClass-rel>|</mo>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ȷkx</mi>
                  <mo class=MathClass-bin>+</mo>
                  <msup>
                     <mrow>
                        <mi>ϕ</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                     </mrow>
                  </msup>
               </mrow>
            </msup>
            <mo class=MathClass-rel>=</mo>
            <mn>2</mn>
            <mo class=MathClass-rel>|</mo>
            <mi>Â</mi>
            <mo class=MathClass-rel>|</mo>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>jϕ</mi>
               </mrow>
            </msup>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mo class=MathClass-open>(</mo>
            <mi mathvariant=italic>kx</mi>
            <mo class=MathClass-bin>+</mo>
            <mi mathvariant=italic>Δϕ</mi>
            <mo class=MathClass-close>)</mo>
         </math>
      <td class=eq-no>(18)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ϕ</mi>
      <mo class=MathClass-rel>=</mo>
      <mrow>
         <mo fence=true form=prefix>(</mo>
         <mrow>
            <msup>
               <mrow>
                  <mi>ϕ</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>+</mo>
               </mrow>
            </msup>
            <mo class=MathClass-bin>+</mo>
            <msup>
               <mrow>
                  <mi>ϕ</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
               </mrow>
            </msup>
         </mrow>
         <mo fence=true form=postfix>)</mo>
      </mrow>
      <mo class=MathClass-bin>∕</mo>
      <mn>2</mn>
   </math>
   is the average phase at
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>Δϕ</mi>
      <mo class=MathClass-rel>=</mo>
      <mrow>
         <mo fence=true form=prefix>(</mo>
         <mrow>
            <msup>
               <mrow>
                  <mi>ϕ</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
               </mrow>
            </msup>
            <mo class=MathClass-bin>−</mo>
            <msup>
               <mrow>
                  <mi>ϕ</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>+</mo>
               </mrow>
            </msup>
         </mrow>
         <mo fence=true form=postfix>)</mo>
      </mrow>
      <mo class=MathClass-bin>∕</mo>
      <mn>2</mn>
   </math>
   is the phase shift between the two waves at
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>. The time signal associated is
<table class=equation>
   <tr>
      <td style="font-size: 9px">
         <math xmlns=http://www.w3.org/1998/Math/MathML class=equation display=block>
            <mi>p</mi>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mi>ℜ</mi>
            <mrow>
               <mo fence=true form=prefix>[</mo>
               <mrow>
                  <mover accent=true>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mo accent=true>^</mo>
                  </mover>
                  <mo class=MathClass-open>(</mo>
                  <mi>x</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mi>ω</mi>
                  <mo class=MathClass-close>)</mo>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>ȷωt</mi>
                     </mrow>
                  </msup>
               </mrow>
               <mo fence=true form=postfix>]</mo>
            </mrow>
            <mo class=MathClass-rel>=</mo>
            <mn>2</mn>
            <mo class=MathClass-rel>|</mo>
            <mi>Â</mi>
            <mo class=MathClass-rel>|</mo>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mo class=MathClass-open>(</mo>
            <mi mathvariant=italic>kx</mi>
            <mo class=MathClass-bin>+</mo>
            <mi mathvariant=italic>Δϕ</mi>
            <mo class=MathClass-close>)</mo>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mo class=MathClass-open>(</mo>
            <mi mathvariant=italic>ωt</mi>
            <mo class=MathClass-bin>+</mo>
            <mi>ϕ</mi>
            <mo class=MathClass-close>)</mo>
         </math>
      <td class=eq-no>(19)
</table>
<p>
   The nature of the standing wave appears in the fact that the oscillation amplitude (envelope curve) is a function of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
   </math>
   and will not change over time. It is noted that there are points where the amplitude of the pressure is always zero (pressure node) and others for which the amplitude is maximum (pressure antinode). It is noteworthy that the distance between two nodes is half a wave length. For particle velocity we have
<table class=equation>
   <tr>
      <td style="font-size: 8px">
         <math xmlns=http://www.w3.org/1998/Math/MathML class=equation display=block>
            <mover accent=true>
               <mrow>
                  <mi>v</mi>
               </mrow>
               <mo accent=true>^</mo>
            </mover>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mo class=MathClass-rel>|</mo>
                  <mi>Â</mi>
                  <mo class=MathClass-rel>|</mo>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi mathvariant=italic>ȷkx</mi>
                        <mo class=MathClass-bin>+</mo>
                        <msup>
                           <mrow>
                              <mi>ϕ</mi>
                           </mrow>
                           <mrow>
                              <mo class=MathClass-bin>+</mo>
                           </mrow>
                        </msup>
                     </mrow>
                  </msup>
                  <mo class=MathClass-bin>−</mo>
                  <mo class=MathClass-rel>|</mo>
                  <mi>Â</mi>
                  <mo class=MathClass-rel>|</mo>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>ȷkx</mi>
                        <mo class=MathClass-bin>+</mo>
                        <msup>
                           <mrow>
                              <mi>ϕ</mi>
                           </mrow>
                           <mrow>
                              <mo class=MathClass-bin>−</mo>
                           </mrow>
                        </msup>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi>c</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mo class=MathClass-bin>−</mo>
            <mn>2</mn>
            <mi>ȷ</mi>
            <mfrac>
               <mrow>
                  <mo class=MathClass-rel>|</mo>
                  <mi>Â</mi>
                  <mo class=MathClass-rel>|</mo>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi>c</mi>
               </mrow>
            </mfrac>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ȷϕ</mi>
               </mrow>
            </msup>
            <mi class=qopname>sin</mi>
            <mo>⁡</mo>
            <mo class=MathClass-open>(</mo>
            <mi mathvariant=italic>kx</mi>
            <mo class=MathClass-bin>+</mo>
            <mi mathvariant=italic>Δϕ</mi>
            <mo class=MathClass-close>)</mo>
         </math>
      <td class=eq-no>(20)
</table>
<p>The time signal associated is
<table class=equation>
   <tr>
      <td style="font-size: 9px">
         <math xmlns=http://www.w3.org/1998/Math/MathML class=equation display=block>
            <mi>v</mi>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mi>ℜ</mi>
            <mrow>
               <mo fence=true form=prefix>[</mo>
               <mrow>
                  <mover accent=true>
                     <mrow>
                        <mi>v</mi>
                     </mrow>
                     <mo accent=true>^</mo>
                  </mover>
                  <mo class=MathClass-open>(</mo>
                  <mi>x</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mi>ω</mi>
                  <mo class=MathClass-close>)</mo>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>ȷωt</mi>
                     </mrow>
                  </msup>
               </mrow>
               <mo fence=true form=postfix>]</mo>
            </mrow>
            <mo class=MathClass-rel>=</mo>
            <mn>2</mn>
            <mfrac>
               <mrow>
                  <mo class=MathClass-rel>|</mo>
                  <mi>Â</mi>
                  <mo class=MathClass-rel>|</mo>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi>c</mi>
               </mrow>
            </mfrac>
            <mi class=qopname>sin</mi>
            <mo>⁡</mo>
            <mo class=MathClass-open>(</mo>
            <mi mathvariant=italic>kx</mi>
            <mo class=MathClass-bin>+</mo>
            <mi mathvariant=italic>Δϕ</mi>
            <mo class=MathClass-close>)</mo>
            <mi class=qopname>sin</mi>
            <mo>⁡</mo>
            <mo class=MathClass-open>(</mo>
            <mi mathvariant=italic>ωt</mi>
            <mo class=MathClass-bin>+</mo>
            <mi>ϕ</mi>
            <mo class=MathClass-close>)</mo>
         </math>
      <td class=eq-no>(21)
</table>
<p>
   It is important to notice that pressure signals and velocity signals are in quadrature (out of phase by
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>π</mi>
      <mo class=MathClass-bin>∕</mo>
      <mn>2</mn>
   </math>
   ) and that pressure nodes corresponds to velocity antinodes and, conversely, velocity nodes to pressure antinodes (see <a href="#" data-img-alt="Figure 1. Progressive waves to the right (A), to the left (B) and standing wave (A + B representing the pressure and A-B the particle velocity) at different time." data-img-src=${config.ASSETS_URL}/01/04/theory/figure-1.svg>Figure 1</a>).
   <h3 class=sectionHead>References</h3>
   <dl class=thebibliography>
     <dt>[1]
     <dd>Leo J. Beranek. Acoustics. The American Institute of Physics, New York, 1986.
     <dt>[2]
     <dd>A.D. Pierce. Acoustics: an Introduction to its Physical Principles and Applications. MCGraw-Hill Book Company, Inc., New York, 1981.
     <dt>[3]
     <dd>Samuel Temkin. Elements of Acoustics. Wiley, New York, 1981.
   </dl>`;

export default theory;
